// Angular or Core
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
// Model
import { ApiResponse } from '@core/models/api-response.model';
import { Pagination } from '@core/models/pagination.model';
import {
  BetLimitCandidate, BetLimitGroupDetail, BetLimitGroupPayload, BetLimitGroupRow, BetLimitMemberRow, BetLimitProviderOption
} from '@core/models/bet-limit-group.model';
// Third Party
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

/**
 * AIO-81599 — Members 1.8 Bet Limit. Stores settings only; nothing reaches SBO2 yet.
 *
 * messages$ is emitted only by delete and export. The setup page shows its own result dialog,
 * and the global "System Message" Swal would pre-empt it.
 */
@Injectable()
export class BetLimitGroupDataService {

  messages$ = new Subject<any[]>();

  constructor(private http: HttpClient) { }

  getOptions(): Observable<BetLimitProviderOption[]> {
    return this.http.get<ApiResponse>(`/betlimitgroup/options`).pipe(
      map(res => res.data.providers)
    );
  }

  getList(query: string): Observable<{ defaults: BetLimitGroupRow[]; rows: BetLimitGroupRow[]; paginations: Pagination }> {
    return this.http.get<ApiResponse>(`/betlimitgroup${query}`).pipe(
      map(res => res.data)
    );
  }

  export(query: string): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`/betlimitgroup/export${query}`).pipe(
      tap(res => this.messages$.next(res.message))
    );
  }

  getById(id: number): Observable<BetLimitGroupDetail> {
    return this.http.get<ApiResponse>(`/betlimitgroup/${id}`).pipe(
      map(res => res.data)
    );
  }

  getMembers(id: number, query: string): Observable<{ rows: BetLimitMemberRow[]; paginations: Pagination }> {
    return this.http.get<ApiResponse>(`/betlimitgroup/${id}/members${query}`).pipe(
      map(res => res.data)
    );
  }

  searchCandidates(query: string): Observable<{ rows: BetLimitCandidate[]; paginations: Pagination }> {
    return this.http.get<ApiResponse>(`/betlimitgroup/members/search${query}`).pipe(
      map(res => res.data)
    );
  }

  resolveUsernames(gameProviderId: number, usernames: string[]): Observable<{ found: BetLimitCandidate[]; not_found: string[] }> {
    return this.http.post<ApiResponse>(`/betlimitgroup/members/resolve`, { game_provider_id: gameProviderId, usernames }).pipe(
      map(res => res.data)
    );
  }

  create(payload: BetLimitGroupPayload): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`/betlimitgroup`, payload);
  }

  update(id: number, payload: BetLimitGroupPayload): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`/betlimitgroup/${id}`, payload);
  }

  delete(id: number): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`/betlimitgroup/${id}`).pipe(
      tap(res => this.messages$.next(res.message))
    );
  }
}
